import { RELEASE_GATE_V2_13_0 } from './releaseStatsigGateConstants.js'

export const USER_PREFERENCES_TABLE = 'user_preferences'

export const USER_PREFERENCES_ORG_SCOPED_GATE = RELEASE_GATE_V2_13_0

/**
 * @param {Function | undefined | null} checkGate
 * @returns {boolean}
 */
export function isOrgScopedUserPreferencesEnabled(checkGate) {
  if (typeof checkGate !== 'function') return false
  return checkGate(USER_PREFERENCES_ORG_SCOPED_GATE)
}

/**
 * @param {{ checkGate?: Function, orgId?: string | null }} params
 * @returns {{ orgScoped: boolean, orgId: string | null }}
 */
export function resolveUserPreferencesScope({ checkGate, orgId = null } = {}) {
  const resolvedOrgId = orgId || window?.Clerk?.organization?.id || null
  return {
    orgScoped: isOrgScopedUserPreferencesEnabled(checkGate),
    orgId: resolvedOrgId,
  }
}

/**
 * @param {import('@supabase/supabase-js').PostgrestFilterBuilder<any, any, any>} query
 * @param {{ userId?: string | null, orgId?: string | null, orgScoped?: boolean }} scope
 */
export function applyUserPreferencesScope(query, { userId, orgId, orgScoped = false }) {
  let scopedQuery = query

  if (userId) {
    scopedQuery = scopedQuery.eq('user_id', userId)
  }

  if (orgScoped && orgId) {
    scopedQuery = scopedQuery.eq('org_id', orgId)
  }

  return scopedQuery
}

function enrichInsertPayload(payload, { orgId, orgScoped }) {
  const enrichRow = (row) => {
    if (!row || typeof row !== 'object') return row
    if (orgScoped && orgId && row.org_id == null) {
      return { ...row, org_id: orgId }
    }
    return row
  }

  if (Array.isArray(payload)) {
    return payload.map(enrichRow)
  }

  return enrichRow(payload)
}

/**
 * @param {{
 *   client: object,
 *   userId?: string | null,
 *   orgId?: string | null,
 *   orgScoped?: boolean,
 *   columns?: string,
 *   maybeSingle?: boolean,
 *   single?: boolean,
 * }} params
 */
export function selectUserPreferences({
  client,
  userId,
  orgId = null,
  orgScoped = false,
  columns = '*',
  maybeSingle = false,
  single = false,
}) {
  let query = applyUserPreferencesScope(client.from(USER_PREFERENCES_TABLE).select(columns), {
    userId,
    orgId,
    orgScoped,
  })

  if (maybeSingle) {
    query = query.maybeSingle()
  } else if (single) {
    query = query.single()
  }

  return query
}

/**
 * @param {{
 *   client: object,
 *   userId?: string | null,
 *   orgId?: string | null,
 *   orgScoped?: boolean,
 *   payload: object | Array<object>,
 *   selectColumns?: string | null,
 * }} params
 */
export function updateUserPreferences({
  client,
  userId,
  orgId = null,
  orgScoped = false,
  payload,
  selectColumns = null,
}) {
  let query = applyUserPreferencesScope(client.from(USER_PREFERENCES_TABLE).update(payload), {
    userId,
    orgId,
    orgScoped,
  })

  if (selectColumns) {
    query = query.select(selectColumns)
  }

  return query
}

/**
 * @param {{
 *   client: object,
 *   payload: object | Array<object>,
 *   orgId?: string | null,
 *   orgScoped?: boolean,
 *   selectColumns?: string | null,
 * }} params
 */
export function insertUserPreferences({
  client,
  payload,
  orgId = null,
  orgScoped = false,
  selectColumns = null,
}) {
  let query = client
    .from(USER_PREFERENCES_TABLE)
    .insert(enrichInsertPayload(payload, { orgId, orgScoped }))

  if (selectColumns) {
    query = query.select(selectColumns)
  }

  return query
}

/**
 * @param {{
 *   client: object,
 *   userId: string,
 *   orgId?: string | null,
 *   orgScoped?: boolean,
 *   columns?: string,
 * }} params
 * @returns {Promise<{ hasRecord: boolean, userRecord: object | null, error: object | null }>}
 */
export async function verifyUserPreferencesRecord({
  client,
  userId,
  orgId = null,
  orgScoped = false,
  columns = '*',
}) {
  const { data, error } = await selectUserPreferences({
    client,
    userId,
    orgId,
    orgScoped,
    columns,
  })

  if (error) {
    return { hasRecord: false, userRecord: null, error }
  }

  const rows = Array.isArray(data) ? data : data ? [data] : []
  return {
    hasRecord: rows.length > 0,
    userRecord: rows[0] || null,
    error: null,
  }
}

/**
 * @param {{
 *   client: object,
 *   userId: string,
 *   orgId?: string | null,
 *   orgScoped?: boolean,
 *   columns?: string,
 * }} params
 * @returns {Promise<{ data: object | null, error: object | null }>}
 */
export async function fetchUserPreferencesRow({
  client,
  userId,
  orgId = null,
  orgScoped = false,
  columns = '*',
}) {
  const { data, error } = await selectUserPreferences({
    client,
    userId,
    orgId,
    orgScoped,
    columns,
    maybeSingle: true,
  })

  return { data: data || null, error: error || null }
}
